'use client';

import { useMemo, useEffect, useState } from 'react';
import {
  Modal,
  Button,
  useToast,
  InputForm,
  IFanConversationSet,
  IFansOfConverstion,
  EasyTab,
  EasyTabItem,
  EasyTabContent,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  useSelectedConversationState,
  useChatTriggerState,
} from '@ui/components';
import { useLoader } from '@ui/contexts/loader-context';
import { useForm } from 'react-hook-form';
import { Trash2, UserPlus, LogOut } from 'lucide-react';
import { Avatar } from '@ui/components';
import { UserSearchModal, ISearchUser } from './user-search-modal';
import { useParams, useRouter, usePathname } from 'next/navigation';

interface GroupSettingsModalProps {
  isOpen: boolean;
  conversationData: IFanConversationSet;
  onClose: () => void;
  useUpdateConversationMutation: any;
  useUpdateConversationParticipantsMutation: any;
  useGetConversationFansQuery: any;
  useLazySearchUsersQuery?: any;
  useDeletePersonalConversationMutation?: any;
  onDeleteSuccess?: () => void;
  currentUserId: string;
}

interface GroupSettingsForm {
  name: string;
  description: string;
}

export function GroupSettingsModal({
  isOpen,
  conversationData,
  onClose,
  useUpdateConversationMutation,
  useUpdateConversationParticipantsMutation,
  useGetConversationFansQuery,
  useLazySearchUsersQuery,
  useDeletePersonalConversationMutation,
  onDeleteSuccess,
  currentUserId,
}: GroupSettingsModalProps) {
  const { creatorSlug } = useParams();
  const router = useRouter();
  const pathname = usePathname();
  const isOnChatsPage = pathname?.includes('/chats');
  const toast = useToast();
  const { openLoader, closeLoader } = useLoader();
  const { flushSelectedConversation } = useSelectedConversationState();
  const { flushChatFanData } = useChatTriggerState();
  const [updateConversation, { isLoading: isUpdating }] = useUpdateConversationMutation();
  const [updateParticipants, { isLoading: isRemoving }] =
    useUpdateConversationParticipantsMutation();
  const [deleteConversation, { isLoading: isDeleting }] = useDeletePersonalConversationMutation();
  const [activeTab, setActiveTab] = useState<'Group Info' | 'Participants' | 'Settings'>(
    'Group Info',
  );
  const [isUserSearchOpen, setIsUserSearchOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const conversationId = conversationData?.conversation?.id;
  const creatorId = conversationData?.conversation?.creator_id;
  const isGroupChat = conversationData?.conversation?.conversation_type === 'group_chat';
  const isCreator = currentUserId === creatorId;

  const { control, handleSubmit, reset, watch } = useForm<GroupSettingsForm>({
    defaultValues: {
      name: conversationData?.conversation?.name || '',
      description: conversationData?.conversation?.description || '',
    },
  });

  useEffect(() => {
    if (conversationData && isOpen) {
      reset({
        name: conversationData?.conversation?.name || '',
        description: conversationData?.conversation?.description || '',
      });
      setActiveTab(isGroupChat ? 'Group Info' : 'Settings');
    }
  }, [conversationData, isOpen, reset, isGroupChat]);

  const {
    data: fansData,
    isLoading: isLoadingFans,
    refetch: refetchFans,
  } = useGetConversationFansQuery(
    {
      id: conversationId,
      page: 1,
      items_per_page: 100,
    },
    {
      skip: !conversationId || !isOpen,
    },
  );

  const participants = useMemo(() => fansData?.fans || [], [fansData?.fans]);

  const isParticipantCreator = (participant: IFansOfConverstion) => {
    return participant.fan_id === creatorId;
  };

  const canRemoveParticipant = (participant: IFansOfConverstion) => {
    return isCreator && !isParticipantCreator(participant);
  };

  const onSaveSettings = handleSubmit(async (data: GroupSettingsForm) => {
    if (!conversationId || !creatorId) return;

    try {
      openLoader();
      await updateConversation({
        id: conversationId,
        name: data.name,
        description: data.description,
        params: { creator_id: creatorId },
      }).unwrap();

      toast({ title: 'Group settings updated successfully' }, { type: 'success' });
      onClose();
    } catch (error) {
      toast({ title: 'Failed to update group settings' }, { type: 'error' });
    } finally {
      closeLoader();
    }
  });

  const handleRemoveParticipant = async (fanId: string) => {
    if (!conversationId || !creatorId) return;

    try {
      openLoader();
      await updateParticipants({
        conversation_id: conversationId,
        fan_ids: [fanId],
        update: 'remove',
        creator_id: creatorId,
      }).unwrap();

      toast({ title: 'Participant removed successfully' }, { type: 'success' });
      refetchFans();
    } catch (error) {
      toast({ title: 'Failed to remove participant' }, { type: 'error' });
    } finally {
      closeLoader();
    }
  };

  const handleLeaveGroup = async () => {
    if (!conversationId || !creatorId || !currentUserId) return;

    try {
      openLoader();
      await updateParticipants({
        conversation_id: conversationId,
        fan_ids: [currentUserId],
        update: 'remove',
        creator_id: creatorId,
      }).unwrap();

      toast({ title: 'You left the group' }, { type: 'success' });
      flushSelectedConversation();
      flushChatFanData();
      onClose();
      onDeleteSuccess?.();

      if (isOnChatsPage && creatorSlug) {
        router.replace(`/creators/${creatorSlug}/chats`, { scroll: false });
      }
    } catch (error) {
      toast({ title: 'Failed to leave group' }, { type: 'error' });
    } finally {
      closeLoader();
    }
  };

  const handleAddUsers = async (users: ISearchUser[]) => {
    if (!conversationId || !creatorId) return;

    try {
      openLoader();
      const fanIds = users.map((u) => u._id).filter(Boolean);
      const userNames = users.map((u) => u.userName || u.displayName).filter(Boolean);
      const participantPayload =
        fanIds.length > 0 ? { fan_ids: fanIds } : { fan_user_names: userNames };
      await updateParticipants({
        conversation_id: conversationId,
        ...participantPayload,
        update: 'add',
        creator_id: creatorId,
      }).unwrap();

      toast({ title: 'Users added successfully' }, { type: 'success' });
      refetchFans();
    } catch (error) {
      toast({ title: 'Failed to add users' }, { type: 'error' });
      throw error;
    } finally {
      closeLoader();
    }
  };

  const existingParticipantIds = useMemo(
    () => participants.map((p: IFansOfConverstion) => p.fan_id),
    [participants],
  );

  const nameValue = watch('name');
  const descriptionValue = watch('description');
  const hasChanges = useMemo(() => {
    return (
      nameValue !== conversationData?.conversation?.name ||
      descriptionValue !== conversationData?.conversation?.description
    );
  }, [nameValue, descriptionValue, conversationData]);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!conversationId || !deleteConversation) return;

    try {
      setIsDeleteConfirmOpen(false);
      openLoader();
      await deleteConversation(conversationId).unwrap();
      flushSelectedConversation();
      flushChatFanData();
      onClose();

      if (isOnChatsPage && creatorSlug) {
        router.replace(`/creators/${creatorSlug}/chats`, { scroll: false });
      }
    } catch (error) {
      toast({ title: 'Failed to delete conversation' }, { type: 'error' });
    } finally {
      closeLoader();
    }
  };

  const modalTitle = 'Manage';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      className="max-w-2xl w-full h-fit max-h-[90vh] overflow-hidden pointer-events-auto">
      <EasyTab
        defaultTab={isGroupChat ? 'Group Info' : 'Settings'}
        onTabChanged={(tab) => setActiveTab(tab as any)}>
        <div className="flex gap-2 border-b border-gray-200 px-6 pt-4">
          {isGroupChat && (
            <>
              <EasyTabItem label="Group Info" className="flex-1" />
              <EasyTabItem label="Participants" className="flex-1" />
            </>
          )}
          <EasyTabItem label="Settings" className="flex-1" />
        </div>

        <form onSubmit={onSaveSettings} className="flex flex-col h-full max-h-[75vh]">
          <div className="flex-1 overflow-y-auto">
            {isGroupChat && (
              <EasyTabContent label="Group Info" className="py-4 space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 flex items-center justify-between">
                    <span>Group Name</span>
                    <span className="text-xs text-note">{nameValue?.length || 0}/50</span>
                  </label>
                  <InputForm
                    form={{
                      control,
                      name: 'name',
                      rules: {
                        required: 'Group name is required',
                        maxLength: {
                          value: 50,
                          message: 'Group name should be less than 50 characters',
                        },
                      },
                    }}
                    placeholder="Enter group name"
                    disabled={!isCreator}
                    maxLength={50}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Description</label>
                  <InputForm
                    form={{
                      control,
                      name: 'description',
                      rules: {
                        maxLength: {
                          value: 200,
                          message: 'Description should be less than 200 characters',
                        },
                      },
                    }}
                    placeholder="Enter group description"
                    isTextArea
                    disabled={!isCreator}
                  />
                </div>
              </EasyTabContent>
            )}

            {isGroupChat && (
              <EasyTabContent label="Participants" className="py-4 flex flex-col h-full">
                {isLoadingFans ? (
                  <div className="text-center py-8 text-gray-500">Loading participants...</div>
                ) : participants.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No participants in this group
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto flex-1">
                    {participants.map((participant: IFansOfConverstion) => {
                      const canRemove = canRemoveParticipant(participant);
                      const isCreatorParticipant = isParticipantCreator(participant);

                      return (
                        <div
                          key={participant.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50">
                          <div className="flex items-center gap-3">
                            <Avatar
                              src={participant.avatar_url}
                              displayName={participant.display_name}
                              size="sm"
                            />
                            <div>
                              <div className="font-medium text-sm">
                                {participant.display_name}
                                {isCreatorParticipant && (
                                  <span className="ml-2 text-xs text-gray-500">(Creator)</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {canRemove && (
                            <Button
                              size="smallIcon"
                              type="icon"
                              onClick={() => handleRemoveParticipant(participant.fan_id)}
                              disabled={isRemoving}
                              className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {useLazySearchUsersQuery && isCreator && (
                  <div className="mt-4 flex justify-center pt-4 border-t border-gray-200">
                    <Button
                      behavior="button"
                      size="small"
                      type="primary"
                      onClick={() => setIsUserSearchOpen(true)}
                      className="w-full sm:w-auto">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Users to Group
                    </Button>
                  </div>
                )}
              </EasyTabContent>
            )}

            <EasyTabContent label="Settings" className="py-4 space-y-6">
              <div className="space-y-4">
                {isCreator && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Delete Conversation</h3>
                    <p className="text-xs text-gray-500 mb-4">
                      Permanently delete this conversation. This action cannot be undone.
                    </p>
                    {useDeletePersonalConversationMutation && (
                      <Button
                        size="small"
                        type="secondary"
                        behavior="button"
                        onClick={handleDeleteClick}
                        disabled={isUpdating || isRemoving || isDeleting}
                        className="w-full sm:w-auto text-red-500 hover:bg-red-50 hover:text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Conversation
                      </Button>
                    )}
                  </div>
                )}
                {!isCreator && isGroupChat && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Leave Group</h3>
                    <p className="text-xs text-gray-500 mb-4">
                      Leave this group. This group will be removed from your list.
                    </p>
                    <Button
                      size="small"
                      type="secondary"
                      behavior="button"
                      onClick={handleLeaveGroup}
                      disabled={isRemoving}
                      className="w-full sm:w-auto text-orange-500 hover:bg-orange-50 hover:text-orange-600">
                      <LogOut className="w-4 h-4 mr-2" />
                      Leave Group
                    </Button>
                  </div>
                )}
              </div>
            </EasyTabContent>
          </div>

          {activeTab === 'Group Info' && isGroupChat && (
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-200 px-6">
              <Button
                size="small"
                type="secondary"
                onClick={onClose}
                disabled={isUpdating || isRemoving || isDeleting}
                className="w-full sm:w-auto">
                Cancel
              </Button>
              {isCreator && (
                <Button
                  size="small"
                  type="primary"
                  disabled={!hasChanges || isUpdating || isRemoving || isDeleting}
                  className="w-full sm:w-auto">
                  {isUpdating ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          )}

          {activeTab === 'Settings' && (
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 px-6">
              <Button
                size="small"
                type="secondary"
                onClick={onClose}
                disabled={isDeleting}
                className="w-full sm:w-auto">
                Close
              </Button>
            </div>
          )}
        </form>
      </EasyTab>
      {useLazySearchUsersQuery && (
        <UserSearchModal
          isOpen={isUserSearchOpen}
          onClose={() => setIsUserSearchOpen(false)}
          onAddUsers={handleAddUsers}
          existingParticipantIds={existingParticipantIds}
          useLazySearchUsersQuery={useLazySearchUsersQuery}
        />
      )}

      <AlertDialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <AlertDialogContent className="z-[99999]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{conversationData?.conversation?.name}&quot;?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-primary hover:bg-primaryHover text-white">
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Modal>
  );
}
